import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FYLKER_MED_BYER, getFylkeName, getByName } from '../data/norwegianCities';
import useFormPersistence from '../hooks/useFormPersistence';

const STEPS = [
  { id: 1, title: 'Kom i gang', icon: 'waving_hand' },
  { id: 2, title: 'Om deg', icon: 'person' },
  { id: 3, title: 'Lokasjon', icon: 'location_on' },
  { id: 4, title: 'Sikkerhet', icon: 'lock' },
];

const RegisterPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showBusinessFields, setShowBusinessFields] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [locating, setLocating] = useState(false);
  
  // Bruk useFormPersistence for å lagre skjemadata lokalt
  const [formData, setFormData, clearFormData] = useFormPersistence('register', {
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    location_fylke: '',
    location_city: '',
    user_type: 'worker',
    age_confirmed: false,
    gdpr_consent: false,
    is_business: false,
    business_name: '',
    org_number: ''
  }, {
    excludeFields: ['password', 'confirmPassword'] // Ikke lagre passord
  });

  const { register } = useAuth();
  const navigate = useNavigate();

  // Hent byer for valgt fylke
  const currentFylke = formData.location_fylke 
    ? FYLKER_MED_BYER.find(f => f.id === formData.location_fylke) 
    : null;
  const byer = currentFylke?.byer || [];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      [name]: type === 'checkbox' ? checked : value
    });
    setError('');
  };

  const handleFylkeChange = (fylkeId) => {
    setFormData({
      location_fylke: fylkeId,
      location_city: '' // Reset by når fylke endres
    });
  };

  const handleByChange = (byId) => {
    setFormData({ location_city: byId });
  };

  // Bruk GPS for å finne lokasjon
  const handleUseLocation = () => {
    setLocating(true);
    
    if (!navigator.geolocation) {
      fetchIPLocation();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        // Importer dynamisk for å unngå sirkulær avhengighet
        const { getByFromCoords } = await import('../data/norwegianCities');
        const result = getByFromCoords(latitude, longitude);
        if (result) {
          setFormData({
            location_fylke: result.fylkeId,
            location_city: result.byId
          });
        }
        setLocating(false);
      },
      () => fetchIPLocation(),
      { timeout: 5000 }
    );
  };

  const fetchIPLocation = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      if (data.latitude && data.longitude) {
        const { getByFromCoords } = await import('../data/norwegianCities');
        const result = getByFromCoords(data.latitude, data.longitude);
        if (result) {
          setFormData({
            location_fylke: result.fylkeId,
            location_city: result.byId
          });
        }
      }
    } catch (err) {
      console.error('IP location error:', err);
    } finally {
      setLocating(false);
    }
  };

  const validateStep = (step) => {
    setError('');
    switch (step) {
      case 1:
        if (!formData.user_type) {
          setError('Velg hva du vil gjøre');
          return false;
        }
        if (!formData.age_confirmed) {
          setError('Du må bekrefte at du er 14 år eller eldre');
          return false;
        }
        break;
      case 2:
        if (!formData.name.trim()) {
          setError('Skriv inn navnet ditt');
          return false;
        }
        if (!formData.email.trim()) {
          setError('Skriv inn e-postadressen din');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          setError('Ugyldig e-postadresse');
          return false;
        }
        if (formData.is_business) {
          if (!formData.business_name.trim()) {
            setError('Skriv inn bedriftsnavn');
            return false;
          }
        }
        break;
      case 3:
        if (!formData.location_fylke) {
          setError('Velg fylke');
          return false;
        }
        if (byer.length > 1 && !formData.location_city) {
          setError('Velg by');
          return false;
        }
        break;
      case 4:
        if (!formData.password) {
          setError('Skriv inn et passord');
          return false;
        }
        if (formData.password.length < 8) {
          setError('Passordet må være minst 8 tegn');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passordene er ikke like');
          return false;
        }
        if (!formData.gdpr_consent) {
          setError('Du må godta vilkårene for å fortsette');
          return false;
        }
        break;
      default:
        break;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setError('');
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    setLoading(true);
    setError('');

    const result = await register({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      password: formData.password,
      location_fylke: formData.location_fylke,
      location_city: formData.location_city || (byer.length === 1 ? byer[0].id : null),
      user_type: formData.user_type,
      is_business: formData.is_business,
      business_name: formData.business_name,
      org_number: formData.org_number,
      age_confirmed: formData.age_confirmed,
      gdpr_consent: formData.gdpr_consent
    });

    if (result.success) {
      clearFormData(); // Fjern lagret data ved suksess
      navigate('/');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <span className="material-symbols-outlined text-5xl text-[#1E3A5F] mb-2 block">waving_hand</span>
              <h2 className="font-heading font-bold text-2xl text-[#0A1929] mb-2">
                Velkommen til Minitask!
              </h2>
              <p className="text-slate-500">
                Den enkleste måten for ungdom å få jobberfaring
              </p>
            </div>

            {/* Brukertype valg */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-3">
                Hva vil du gjøre?
              </label>
              <div className="grid grid-cols-1 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ user_type: 'worker' })}
                  className={`p-5 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                    formData.user_type === 'worker'
                      ? 'border-[#0A1929] bg-[#0A1929]/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    formData.user_type === 'worker' ? 'bg-[#0A1929] text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    <span className="material-symbols-outlined text-2xl">person_search</span>
                  </div>
                  <div>
                    <span className="font-semibold text-[#0A1929] block">Jeg vil finne oppdrag</span>
                    <span className="text-sm text-slate-500">Utfør oppgaver og tjen penger</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ user_type: 'poster' })}
                  className={`p-5 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                    formData.user_type === 'poster'
                      ? 'border-[#0A1929] bg-[#0A1929]/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    formData.user_type === 'poster' ? 'bg-[#0A1929] text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    <span className="material-symbols-outlined text-2xl">post_add</span>
                  </div>
                  <div>
                    <span className="font-semibold text-[#0A1929] block">Jeg vil legge ut oppdrag</span>
                    <span className="text-sm text-slate-500">Finn noen som kan hjelpe deg</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Aldersbekreftelse */}
            <div className="bg-blue-50 rounded-xl p-4">
              <label className="flex items-start gap-4 cursor-pointer">
                <div className="pt-0.5">
                  <input
                    type="checkbox"
                    name="age_confirmed"
                    checked={formData.age_confirmed}
                    onChange={handleChange}
                    className="w-6 h-6 rounded border-slate-300 text-[#0A1929] focus:ring-[#1E3A5F]"
                  />
                </div>
                <div>
                  <span className="font-medium text-[#0A1929] block">
                    Jeg bekrefter at jeg er 14 år eller eldre
                  </span>
                  <span className="text-sm text-slate-500">
                    Minitask er for ungdom fra 14 år og oppover
                  </span>
                </div>
              </label>
            </div>

            {/* Bedriftsknapp - diskret i venstre hjørne */}
            <div className="pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowBusinessFields(!showBusinessFields);
                  setFormData({ is_business: !formData.is_business });
                }}
                className="text-sm text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors"
              >
                <span className="material-symbols-outlined text-base">
                  {formData.is_business ? 'check_box' : 'check_box_outline_blank'}
                </span>
                Registrer som bedrift
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-5">
            <div className="text-center mb-6">
              <h2 className="font-heading font-bold text-xl text-[#0A1929] mb-1">
                Fortell oss om deg
              </h2>
              <p className="text-slate-500 text-sm">
                {formData.is_business ? 'Bedriftsinformasjon' : 'Din personlige informasjon'}
              </p>
            </div>

            {/* Bedriftsfelt */}
            {formData.is_business && (
              <div className="bg-amber-50 rounded-xl p-4 space-y-4 mb-4">
                <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                  <span className="material-symbols-outlined">business</span>
                  Bedriftsinformasjon
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A1929] mb-2">
                    Bedriftsnavn *
                  </label>
                  <input
                    type="text"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                    placeholder="Bedriftens navn"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A1929] mb-2">
                    Organisasjonsnummer <span className="text-slate-400 font-normal">(valgfritt)</span>
                  </label>
                  <input
                    type="text"
                    name="org_number"
                    value={formData.org_number}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                    placeholder="9 siffer"
                    maxLength={9}
                  />
                </div>
              </div>
            )}

            {/* Navn */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                {formData.is_business ? 'Kontaktperson' : 'Fullt navn'} *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400">
                  person
                </span>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                  placeholder="Ola Nordmann"
                />
              </div>
            </div>

            {/* E-post */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                E-postadresse *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400">
                  email
                </span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                  placeholder="din@epost.no"
                />
              </div>
            </div>

            {/* Telefon */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                Telefonnummer <span className="text-slate-400 font-normal">(valgfritt)</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400">
                  phone
                </span>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                  placeholder="12345678"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-5">
            <div className="text-center mb-6">
              <h2 className="font-heading font-bold text-xl text-[#0A1929] mb-1">
                Hvor holder du til?
              </h2>
              <p className="text-slate-500 text-sm">
                Vi viser deg oppdrag i nærheten av deg
              </p>
            </div>

            {/* Bruk min posisjon knapp */}
            <button
              type="button"
              onClick={handleUseLocation}
              disabled={locating}
              className={`w-full flex items-center justify-center gap-2 px-4 py-4 rounded-xl text-sm font-medium transition-all ${
                locating
                  ? 'bg-slate-100 text-slate-400'
                  : 'bg-gradient-to-r from-[#1E3A5F] to-[#0A1929] text-white shadow-lg hover:shadow-xl'
              }`}
            >
              <span className={`material-symbols-outlined text-xl ${locating ? 'animate-pulse' : ''}`}>
                {locating ? 'location_searching' : 'my_location'}
              </span>
              {locating ? 'Finner din posisjon...' : 'Bruk min nåværende posisjon'}
            </button>

            <div className="text-center text-slate-400 text-sm">eller velg manuelt</div>

            {/* Fylke */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                Fylke *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400">
                  map
                </span>
                <select
                  name="location_fylke"
                  value={formData.location_fylke}
                  onChange={(e) => handleFylkeChange(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent appearance-none bg-white"
                >
                  <option value="">Velg fylke</option>
                  {FYLKER_MED_BYER.map(fylke => (
                    <option key={fylke.id} value={fylke.id}>{fylke.name}</option>
                  ))}
                </select>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 pointer-events-none">
                  expand_more
                </span>
              </div>
            </div>

            {/* By - vises kun hvis fylke har flere byer */}
            {formData.location_fylke && byer.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">
                  By *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400">
                    location_city
                  </span>
                  <select
                    name="location_city"
                    value={formData.location_city}
                    onChange={(e) => handleByChange(e.target.value)}
                    className="w-full pl-10 pr-10 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent appearance-none bg-white"
                  >
                    <option value="">Velg by</option>
                    {byer.map(by => (
                      <option key={by.id} value={by.id}>{by.name}</option>
                    ))}
                  </select>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 pointer-events-none">
                    expand_more
                  </span>
                </div>
              </div>
            )}

            {/* Vis valgt lokasjon */}
            {formData.location_fylke && (formData.location_city || byer.length === 1) && (
              <div className="flex items-center gap-2 p-4 bg-green-50 rounded-xl text-green-700">
                <span className="material-symbols-outlined">check_circle</span>
                <span className="font-medium">
                  {formData.location_city 
                    ? `${getByName(formData.location_city)}, ${getFylkeName(formData.location_fylke)}`
                    : getFylkeName(formData.location_fylke)
                  }
                </span>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-5">
            <div className="text-center mb-6">
              <h2 className="font-heading font-bold text-xl text-[#0A1929] mb-1">
                Sikre kontoen din
              </h2>
              <p className="text-slate-500 text-sm">
                Velg et sterkt passord
              </p>
            </div>

            {/* Passord */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                Passord *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400">
                  lock
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  minLength={8}
                  className="w-full pl-10 pr-12 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                  placeholder="Minst 8 tegn"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                >
                  <span className="material-symbols-outlined">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
            </div>

            {/* Bekreft passord */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                Bekreft passord *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400">
                  lock
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                  placeholder="Gjenta passordet"
                />
              </div>
              {formData.password && formData.confirmPassword && (
                <div className={`flex items-center gap-1 mt-2 text-sm ${
                  formData.password === formData.confirmPassword ? 'text-green-600' : 'text-red-500'
                }`}>
                  <span className="material-symbols-outlined text-base">
                    {formData.password === formData.confirmPassword ? 'check_circle' : 'error'}
                  </span>
                  {formData.password === formData.confirmPassword ? 'Passordene er like' : 'Passordene er ikke like'}
                </div>
              )}
            </div>

            {/* GDPR samtykke - stor og mobilvennlig */}
            <div className="bg-slate-50 rounded-xl p-4 mt-6">
              <label className="flex items-start gap-4 cursor-pointer">
                <div className="pt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    name="gdpr_consent"
                    checked={formData.gdpr_consent}
                    onChange={handleChange}
                    className="w-6 h-6 rounded border-slate-300 text-[#0A1929] focus:ring-[#1E3A5F]"
                  />
                </div>
                <div className="flex-1">
                  <span className="font-medium text-[#0A1929] block mb-1">
                    Jeg godtar vilkårene
                  </span>
                  <span className="text-sm text-slate-500 leading-relaxed">
                    Jeg har lest og godtar{' '}
                    <Link to="/personvern" className="text-[#1E3A5F] underline font-medium" target="_blank">
                      personvernerklæringen
                    </Link>{' '}
                    og samtykker til behandling av mine personopplysninger.
                  </span>
                </div>
              </label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => currentStep === 1 ? navigate('/') : prevStep()}
              className="flex items-center gap-1 text-[#1E3A5F] hover:text-[#0A1929] transition-colors p-2 -ml-2"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              <span className="hidden sm:inline text-sm">
                {currentStep === 1 ? 'Avbryt' : 'Tilbake'}
              </span>
            </button>
            
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0A1929] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">M</span>
              </div>
              <span className="font-heading font-bold text-lg text-[#0A1929] hidden sm:inline">Minitask</span>
            </Link>
            
            <div className="text-right min-w-[60px]">
              <span className="text-xs text-slate-400">Steg</span>
              <p className="font-semibold text-[#0A1929]">{currentStep}/4</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="flex gap-1.5 mt-3">
            {STEPS.map((step) => (
              <div
                key={step.id}
                className={`flex-1 h-1.5 rounded-full transition-colors ${
                  step.id <= currentStep ? 'bg-[#1E3A5F]' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-lg mx-auto px-4 py-6">
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
              <span className="material-symbols-outlined">error</span>
              {error}
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6">
            {renderStepContent()}
          </div>

          {/* Login link */}
          <div className="mt-6 text-center">
            <p className="text-slate-500 text-sm">
              Har du allerede en konto?{' '}
              <Link to="/logg-inn" className="text-[#1E3A5F] font-medium hover:underline">
                Logg inn
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Navigation */}
      <div className="bg-white border-t border-slate-200 p-4 pb-safe sticky bottom-0 z-50">
        <div className="max-w-lg mx-auto flex gap-3">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={prevStep}
              className="flex-1 bg-slate-100 text-[#0A1929] py-4 rounded-xl font-medium hover:bg-slate-200 transition-colors flex items-center justify-center gap-2 text-base"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              <span className="hidden sm:inline">Tilbake</span>
              <span className="sm:hidden">Tilbake</span>
            </button>
          )}
          
          {currentStep < 4 ? (
            <button
              type="button"
              onClick={nextStep}
              className="flex-1 bg-[#0A1929] text-white py-4 rounded-xl font-medium hover:bg-[#1E3A5F] transition-colors flex items-center justify-center gap-2 text-base shadow-lg"
            >
              <span className="sm:hidden">Neste</span>
              <span className="hidden sm:inline">Neste</span>
              <span className="material-symbols-outlined">arrow_forward</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-[#1E3A5F] to-[#0A1929] text-white py-4 rounded-xl font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base shadow-lg"
            >
              {loading ? (
                <>
                  <span className="animate-spin material-symbols-outlined">progress_activity</span>
                  Oppretter konto...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined">check</span>
                  Opprett konto
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
